import { showInputBubble } from './input-bubble';

/**
 * Validación GLOBAL de lo que se teclea o pega en los campos del panel,
 * con una burbuja que explica qué espera el campo (en vez de que la tecla
 * "no haga nada" y el cliente no entienda por qué):
 *
 *  - Teléfono (type="tel" o data-allow="phone"): números, espacios, + - ( ).
 *  - Nombres (data-allow="letters", o campos cuyo id termina en "Name"/"-name"):
 *    letras (con tildes y ñ), espacios, punto, apóstrofo y guion.
 *  - Números (type="number" o data-allow="digits"): solo dígitos (con punto
 *    decimal si el step lo permite, y signo menos si el mínimo es negativo);
 *    además avisa si se supera el máximo (atributo max) y ajusta al máximo.
 *  - Correo (type="email"): sin espacios.
 *
 * Funciona por delegación en `document`, así que cubre modales y contenido
 * creado después. data-no-guard en un campo lo excluye.
 * Llamar una vez al iniciar la app: initInputGuards();
 */
type Rule = { test: RegExp; message: string };

const RULES: Record<string, Rule> = {
  phone: { test: /^[0-9+\-() ]*$/, message: 'El teléfono solo admite números (y +, - o espacios).' },
  letters: { test: /^[A-Za-zÀ-ÖØ-öø-ÿÑñ' .\-]*$/, message: 'Este campo solo admite letras.' },
  digits: { test: /^[0-9]*$/, message: 'Escribe solo números.' },
  email: { test: /^\S*$/, message: 'El correo electrónico no lleva espacios.' },
};

/** Regla de un campo numérico según su step (decimales) y min (negativos). */
const numberRule = (el: HTMLInputElement): Rule => {
  const step = (el.getAttribute('step') || '').trim();
  const decimals = step === 'any' || (step !== '' && !Number.isInteger(Number(step))) || el.inputMode === 'decimal';
  const negative = el.min !== '' && Number(el.min) < 0;
  if (!decimals && !negative) return RULES['digits'];
  const chars = `0-9${decimals ? '.' : ''}${negative ? '\\-' : ''}`;
  return {
    test: new RegExp(`^[${chars}]*$`),
    message: decimals ? 'Escribe solo números (usa punto para los decimales).' : 'Escribe solo números.',
  };
};

const ruleFor = (el: HTMLInputElement): Rule | null => {
  if (el.hasAttribute('data-no-guard') || el.readOnly || el.disabled) return null;
  const explicit = el.getAttribute('data-allow');
  if (explicit && RULES[explicit]) return RULES[explicit];
  if (el.type === 'tel') return RULES['phone'];
  if (el.type === 'number') return numberRule(el);
  if (el.type === 'email') return RULES['email'];
  if (el.type === 'text' && /(Name|-name)$/.test(el.id || '') && !el.closest('app-card-payment-form')) return RULES['letters'];
  return null;
};

export function initInputGuards(): void {
  // Campos numéricos: el navegador descarta en silencio las letras (no
  // siempre llega beforeinput); se revisa la tecla para poder avisar.
  document.addEventListener('keydown', (event: KeyboardEvent) => {
    const el = event.target;
    if (!(el instanceof HTMLInputElement) || el.type !== 'number') return;
    if (event.key.length !== 1 || event.ctrlKey || event.metaKey || event.altKey) return;
    const rule = ruleFor(el);
    if (!rule || rule.test.test(event.key)) return;
    event.preventDefault();
    showInputBubble(el, rule.message);
  }, true);

  document.addEventListener('beforeinput', (event: Event) => {
    const e = event as InputEvent;
    const el = e.target;
    if (!(el instanceof HTMLInputElement)) return;
    const rule = ruleFor(el);
    if (!rule || !e.data) return;
    if (!rule.test.test(e.data)) {
      e.preventDefault();
      showInputBubble(el, rule.message);
    }
  }, true);

  // Pegar texto: se limpia lo que no corresponde y se avisa.
  document.addEventListener('paste', (event: ClipboardEvent) => {
    const el = event.target;
    if (!(el instanceof HTMLInputElement)) return;
    const rule = ruleFor(el);
    const text = event.clipboardData?.getData('text') ?? '';
    if (!rule || rule.test.test(text)) return;
    event.preventDefault();
    const cleaned = [...text].filter((ch) => rule.test.test(ch)).join('');
    el.setRangeText(cleaned, el.selectionStart ?? el.value.length, el.selectionEnd ?? el.value.length, 'end');
    el.dispatchEvent(new Event('input', { bubbles: true }));
    showInputBubble(el, `${rule.message} Quitamos los caracteres no válidos.`);
  }, true);

  // Números con máximo/mínimo: se avisa y se ajusta al rango.
  document.addEventListener('input', (event: Event) => {
    const el = event.target;
    if (!(el instanceof HTMLInputElement) || el.type !== 'number' || el.hasAttribute('data-no-guard')) return;
    const value = Number(el.value);
    if (el.value === '' || !Number.isFinite(value)) return;
    const max = el.max !== '' ? Number(el.max) : NaN;
    const min = el.min !== '' ? Number(el.min) : NaN;
    if (Number.isFinite(max) && value > max) {
      el.value = String(max);
      el.dispatchEvent(new Event('input', { bubbles: true }));
      showInputBubble(el, el.getAttribute('data-max-message') || `El máximo permitido es ${max}.`);
    } else if (Number.isFinite(min) && value < min) {
      showInputBubble(el, el.getAttribute('data-min-message') || `El mínimo permitido es ${min}.`, 'info');
    }
  }, true);
}
